import { useState, useEffect, useRef, useCallback } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// SHEET URLs  (CSV — works without API key, sheets must be published)
// ─────────────────────────────────────────────────────────────────────────────
const SHEETS = {
  members: "https://docs.google.com/spreadsheets/d/19O_k-edSACFizST2NczqGcst1qZo3KtdQQB-WwfInZ4/gviz/tq?tqx=out:csv&sheet=Medlemmer",
  board:   "https://docs.google.com/spreadsheets/d/1HxUZsXoYKNLyYtJVkAf8XR2ewN9NxUgCfz5zUpTqcIg/gviz/tq?tqx=out:csv&sheet=Bestyrelse",
  news:    "https://docs.google.com/spreadsheets/d/1G2AlwlAZZeQHEoskWhPzyrWPtvqmtb_FwCplLr5VeKM/gviz/tq?tqx=out:csv&sheet=Nyheder",
};

// ─────────────────────────────────────────────────────────────────────────────
// FREE APIs
// ─────────────────────────────────────────────────────────────────────────────
const WEATHER_URL =
  "https://api.open-meteo.com/v1/forecast?latitude=55.4761&longitude=8.4594" +
  "&current=temperature_2m,weathercode,windspeed_10m,relative_humidity_2m" +
  "&wind_speed_unit=ms&timezone=Europe%2FCopenhagen";

const PRAYER_URL =
  "https://api.aladhan.com/v1/timingsByCity?city=Esbjerg&country=Denmark&method=3";

// ─────────────────────────────────────────────────────────────────────────────
// LOOKUP TABLES
// ─────────────────────────────────────────────────────────────────────────────
const WMO_ICONS = {
  0:"☀️",1:"🌤",2:"⛅",3:"☁️",45:"🌫",48:"🌫",51:"🌦",53:"🌦",55:"🌧",
  61:"🌧",63:"🌧",65:"🌧",71:"🌨",73:"🌨",75:"❄️",80:"🌦",81:"🌧",82:"⛈",
  95:"⛈",96:"⛈",99:"⛈",
};
const WMO_TR = {
  0:"Açık",1:"Az Bulutlu",2:"Parçalı Bulutlu",3:"Bulutlu",45:"Sisli",48:"Sisli",
  51:"Hafif Yağmurlu",53:"Yağmurlu",55:"Kuvvetli Yağmur",
  61:"Hafif Yağmurlu",63:"Yağmurlu",65:"Kuvvetli Yağmur",
  71:"Hafif Karlı",73:"Karlı",75:"Yoğun Kar",80:"Sağanak",
  81:"Kuvvetli Sağanak",82:"Şiddetli Sağanak",
  95:"Gök Gürültülü",96:"Fırtına",99:"Fırtına",
};
const WMO_DA = {
  0:"Klar himmel",1:"Mest klart",2:"Delvis skyet",3:"Overskyet",
  45:"Tåge",48:"Tåge",51:"Let regn",53:"Regn",55:"Kraftig regn",
  61:"Let regn",63:"Regn",65:"Kraftig regn",71:"Let sne",73:"Sne",
  75:"Kraftig sne",80:"Byger",81:"Kraftige byger",82:"Voldsomme byger",
  95:"Tordenvejr",96:"Tordenvejr",99:"Tordenvejr",
};

const PRAYER_SLOTS = [
  { key:"Imsak",   tr:"İmsak"  },
  { key:"Dhuhr",   tr:"Öğle"   },
  { key:"Asr",     tr:"İkindi" },
  { key:"Maghrib", tr:"Akşam"  },
  { key:"Isha",    tr:"Yatsı"  },
];
const NEXT_KEYS = ["Fajr","Dhuhr","Asr","Maghrib","Isha"];
const YEARS     = ["2025","2026 (1-6)","2026 (7-12)"];
const PPS       = 40;

// ─────────────────────────────────────────────────────────────────────────────
// DESIGN TOKENS
// ─────────────────────────────────────────────────────────────────────────────
const C = {
  bark:       "#2A1D0E",
  canvas:     "#F0EBE2",
  surface:    "#FAF7F3",
  white:      "#FFFFFF",
  gold:       "#B8832A",
  goldBg:     "rgba(184,131,42,0.12)",
  goldBorder: "rgba(184,131,42,0.32)",
  ink:        "#1C1208",
  inkMid:     "#3A2A14",
  inkBody:    "#4A3820",
  inkMuted:   "#7A6A52",
  inkFaint:   "#A89880",
  rule:       "rgba(44,28,8,0.08)",
  ruleMed:    "rgba(44,28,8,0.13)",
  paidBg:     "#D8EFD8", paidFg:   "#145420",
  unpaidBg:   "#FAE2DF", unpaidFg: "#7A1E18",
  rowA:       "#FAF7F3",
  rowB:       "#FFFFFF",
};
const sp = { xs:8, sm:13, md:21, lg:34, xl:55 };

// ─────────────────────────────────────────────────────────────────────────────
// CSV PARSER  — handles quoted fields, \r\n line endings
// ─────────────────────────────────────────────────────────────────────────────
function parseCSVLine(line) {
  const fields = [];
  let cur = "", inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQ && line[i+1] === '"') { cur += '"'; i++; }
      else inQ = !inQ;
    } else if (ch === ',' && !inQ) {
      fields.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  fields.push(cur);
  return fields;
}

function parseCSV(raw) {
  const lines = raw.replace(/\r\n/g,"\n").replace(/\r/g,"\n").trim().split("\n");
  if (lines.length < 2) return [];
  const headers = parseCSVLine(lines[0]);
  return lines.slice(1).map(line => {
    const vals = parseCSVLine(line);
    return Object.fromEntries(headers.map((h,i) => [h.trim(), (vals[i] ?? "").trim()]));
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// MEMBERS  — parsed positionally because header row has no column names
// Members CSV format (confirmed from sheet):
//   col 0 = row number, col 1 = Navn, col 2 = 2025, col 3 = 2026(1-6), col 4 = 2026(7-12)
// ─────────────────────────────────────────────────────────────────────────────
function parseMembersCSV(raw) {
  const lines = raw.replace(/\r\n/g,"\n").replace(/\r/g,"\n").trim().split("\n");
  // Skip header row (line 0), parse from line 1
  return lines.slice(1).map(line => {
    const cols = parseCSVLine(line);
    return {
      No:          (cols[0] ?? "").trim(),
      Navn:        (cols[1] ?? "").trim(),
      "2025":      (cols[2] ?? "").trim(),
      "2026 (1-6)":(cols[3] ?? "").trim(),
      "2026 (7-12)":(cols[4] ?? "").trim(),
    };
  }).filter(m => m.Navn !== "");
}

function sortByLastName(arr) {
  return [...arr].sort((a, b) => {
    const last = n => {
      const p = String(n.Navn||"").trim().split(" ");
      return p.length > 1 ? p[p.length-1] : p[0];
    };
    const la = last(a), lb = last(b);
    if (la !== lb) return la.localeCompare(lb, "tr");
    return String(a.Navn||"").localeCompare(String(b.Navn||""), "tr");
  });
}

function parseEventDate(s) {
  if (!s) return null;
  const m = String(s).match(/(\d{1,2})[.\/\-](\d{1,2})[.\/\-](\d{2,4})/);
  if (!m) return null;
  const y = m[3].length===2 ? 2000+parseInt(m[3]) : parseInt(m[3]);
  return new Date(y, parseInt(m[2])-1, parseInt(m[1]));
}

function toMin(t) {
  if (!t) return 9999;
  const [h,m] = String(t).split(":").map(Number);
  return h*60+m;
}

function minsUntil(t) {
  if (!t) return null;
  const now = new Date();
  let d = toMin(t) - (now.getHours()*60 + now.getMinutes());
  return d < 0 ? d+1440 : d;
}

function fmtMins(m) {
  if (m===null) return "–";
  return m >= 60 ? `${Math.floor(m/60)}t ${m%60}m` : `${m}m`;
}

// TRUE/FALSE strings from Google Sheets CSV checkboxes
function payStatus(val) {
  const v = String(val||"").trim().toUpperCase();
  if (v === "TRUE")  return "paid";
  if (v === "FALSE") return "unpaid";
  return "none";
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function MoskeDashboard() {
  const [time,       setTime]       = useState(new Date());
  const [weather,    setWeather]    = useState(null);
  const [prayers,    setPrayers]    = useState(null);
  const [nextPrayer, setNextPrayer] = useState(null);
  const [minsLeft,   setMinsLeft]   = useState(null);
  const [members,    setMembers]    = useState([]);
  const [news,       setNews]       = useState([]);
  const [board,      setBoard]      = useState([]);

  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const animRef  = useRef(null);
  const posRef   = useRef(0);
  const lastTRef = useRef(null);

  // Clock
  useEffect(() => {
    const id = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Weather
  useEffect(() => {
    const go = () => fetch(WEATHER_URL).then(r=>r.json()).then(d=>setWeather(d.current)).catch(()=>{});
    go();
    const id = setInterval(go, 600_000);
    return () => clearInterval(id);
  }, []);

  // Prayer times
  const loadPrayers = useCallback(() => {
    fetch(PRAYER_URL).then(r=>r.json()).then(d=>setPrayers(d.data?.timings??null)).catch(()=>{});
  }, []);
  useEffect(() => {
    loadPrayers();
    const now = new Date();
    const ms = new Date(now.getFullYear(), now.getMonth(), now.getDate()+1) - now;
    const t = setTimeout(() => { loadPrayers(); setInterval(loadPrayers, 86_400_000); }, ms);
    return () => clearTimeout(t);
  }, [loadPrayers]);

  // Next prayer countdown
  useEffect(() => {
    if (!prayers) return;
    const nowMin = time.getHours()*60 + time.getMinutes();
    let found = null;
    for (const p of NEXT_KEYS) {
      if (prayers[p] && toMin(prayers[p]) > nowMin) { found = p; break; }
    }
    if (!found) found = NEXT_KEYS[0];
    setNextPrayer(found);
    setMinsLeft(minsUntil(prayers[found]));
  }, [prayers, time]);

  // Google Sheets
  useEffect(() => {
    const loadMembers = () =>
      fetch(SHEETS.members).then(r=>r.text()).then(raw => {
        const rows = parseMembersCSV(raw);
        if (rows.length) setMembers(sortByLastName(rows));
      }).catch(()=>{});

    const loadBoard = () =>
      fetch(SHEETS.board).then(r=>r.text()).then(raw => {
        const rows = parseCSV(raw);
        if (rows.length) setBoard(rows);
      }).catch(()=>{});

    const loadNews = () =>
      fetch(SHEETS.news).then(r=>r.text()).then(raw => {
        const rows = parseCSV(raw);
        if (rows.length) setNews(rows);
      }).catch(()=>{});

    loadMembers(); loadBoard(); loadNews();
    const id = setInterval(() => { loadMembers(); loadBoard(); loadNews(); }, 60_000);
    return () => clearInterval(id);
  }, []);

  // Seamless infinite scroll
  useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner || members.length === 0) return;
    posRef.current = 0; lastTRef.current = null; outer.scrollTop = 0;
    const tick = ts => {
      if (lastTRef.current !== null) {
        posRef.current += PPS * (ts - lastTRef.current) / 1000;
        const half = inner.scrollHeight / 2;
        if (posRef.current >= half) posRef.current -= half;
        outer.scrollTop = posRef.current;
      }
      lastTRef.current = ts;
      animRef.current  = requestAnimationFrame(tick);
    };
    animRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animRef.current);
  }, [members]);

  // Derived
  const today = new Date(); today.setHours(0,0,0,0);
  const upcoming = [...news]
    .map(n => ({ ...n, _d: parseEventDate(n.Dato) }))
    .filter(n => n._d && n._d >= today)
    .sort((a,b) => a._d - b._d)
    .slice(0, 5);

  const clock    = d => d.toLocaleTimeString("da-DK", { hour:"2-digit", minute:"2-digit", second:"2-digit" });
  const fullDate = d => d.toLocaleDateString("da-DK",  { weekday:"long", day:"numeric", month:"long", year:"numeric" });
  const evtDate  = s => {
    const d = parseEventDate(s);
    return d ? d.toLocaleDateString("da-DK", { weekday:"short", day:"numeric", month:"short" }) : s;
  };

  const nowMin   = time.getHours()*60 + time.getMinutes();
  const nextSlot = PRAYER_SLOTS.find(p => p.key === nextPrayer);

  return (
    <div style={st.root}>

      {/* ══ HEADER ══════════════════════════════════════════════════════ */}
      <header style={st.bar}>
        <div style={st.barName}>
          <span style={st.barTitle}>🕌 Esbjerg Anatoliens Moske</span>
          <span style={st.barDate}>{fullDate(time)}</span>
        </div>

        <div style={st.wCard}>
          <span style={{ fontSize:36, lineHeight:1 }}>
            {weather ? (WMO_ICONS[weather.weathercode]??"🌡") : "🌡"}
          </span>
          <div style={st.wBody}>
            <span style={st.wTemp}>{weather ? `${Math.round(weather.temperature_2m)}°C` : "–"}</span>
            <span style={st.wDesc}>
              {weather ? `${WMO_TR[weather.weathercode]??""} · ${WMO_DA[weather.weathercode]??""}` : "Henter vejr…"}
            </span>
            <div style={st.wMeta}>
              {weather?.windspeed_10m        != null && <span>💨 {Math.round(weather.windspeed_10m)} m/s</span>}
              {weather?.relative_humidity_2m != null && <span>💧 {Math.round(weather.relative_humidity_2m)}%</span>}
              <span style={st.wClock}>{clock(time)}</span>
            </div>
          </div>
        </div>

        <div style={st.cCard}>
          <span style={st.cLabel}>Sonraki namaza · Næste bøn om</span>
          <span style={st.cMins}>{fmtMins(minsLeft)}</span>
          {nextSlot && <span style={st.cName}>{nextSlot.tr}</span>}
        </div>
      </header>

      {/* ══ MAIN ════════════════════════════════════════════════════════ */}
      <main style={st.main}>

        {/* LEFT */}
        <section style={st.left}>

          <SectionHead title="Kommende arrangementer · Yaklaşan Etkinlikler" />
          <div style={st.evList}>
            {upcoming.length === 0 && <p style={st.empty}>Ingen kommende arrangementer</p>}
            {upcoming.map((n,i) => (
              <div key={i} style={st.evCard}>
                <div style={st.evBadge}>{evtDate(n.Dato)}</div>
                <div style={st.evBody}>
                  <span style={st.evTitle}>{n.Titel}</span>
                  {n.TurkishTitel && n.TurkishTitel !== n.Titel &&
                    <span style={st.evTitleTR}>{n.TurkishTitel}</span>}
                  {n.Tekst       && <span style={st.evText}>{n.Tekst}</span>}
                  {n.TurkishTekst && <span style={st.evTextTR}>{n.TurkishTekst}</span>}
                </div>
              </div>
            ))}
          </div>

          <div style={{ flex:1 }} />

          {/* Prayer times */}
          <div style={st.prayerWrap}>
            <SectionHead title="Bugünkü Namaz Vakitleri · Bøntider i dag" />
            <div style={st.prayerGrid}>
              {PRAYER_SLOTS.map(({ key, tr }) => {
                const ts     = prayers?.[key] ?? "–";
                const isNext = nextPrayer === key;
                const isPast = prayers?.[key] && toMin(prayers[key]) < nowMin && !isNext;
                return (
                  <div key={key} style={{
                    ...st.prayerRow,
                    ...(isNext ? st.prayerActive : {}),
                  }}>
                    {isNext && <span style={st.prayerAccent} />}
                    <span style={{ ...st.prayerName, color: isNext ? C.gold : isPast ? "#8A7050" : "#2A1D0E", fontWeight: isNext ? 700 : 600 }}>
                      {tr}
                    </span>
                    <span style={{ ...st.prayerTime, color: isNext ? C.gold : isPast ? "#8A7050" : "#2A1D0E", fontWeight: isNext ? 700 : 600 }}>
                      {ts}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Board */}
          <div style={st.boardWrap}>
            <SectionHead title="Yönetim Kurulu · Bestyrelsen" />
            <div style={st.boardGrid}>
              {board.map((b,i) => (
                <div key={i} style={st.boardCard}>
                  <div style={st.boardRoleLine}>
                    <span style={st.boardRoleTR}>{b.RolleTR ?? ""}</span>
                    <span style={st.boardRoleSep}>·</span>
                    <span style={st.boardRoleDA}>{b.RolleDA ?? ""}</span>
                  </div>
                  {/* Board sheet has "Navn " with trailing space */}
                  <span style={st.boardName}>{b["Navn "] ?? b.Navn ?? ""}</span>
                  {b.Telefon && <span style={st.boardPhone}>{b.Telefon}</span>}
                </div>
              ))}
            </div>
          </div>

        </section>

        {/* RIGHT — Members */}
        <section style={st.right}>
          <div style={st.sHead}>
            <span style={st.sTitle}>Medlemmer · Üyeler</span>
            <span style={st.sSub}>{members.length} i alt</span>
          </div>

          <div style={st.tHead}>
            <span style={{ ...st.col, ...st.cNo }}>No.</span>
            <span style={{ ...st.col, flex:1 }}>Navn / İsim</span>
            {YEARS.map(y => <span key={y} style={{ ...st.col, ...st.cYr }}>{y}</span>)}
          </div>

          <div ref={outerRef} style={st.scrollOuter}>
            <div ref={innerRef}>
              {[...members, ...members].map((m,i) => (
                <div key={i} style={{ ...st.mRow, background: i%2===0 ? C.rowA : C.rowB }}>
                  <span style={{ ...st.col, ...st.cNo, color:C.inkMuted, fontSize:13 }}>{m.No}</span>
                  <span style={{ ...st.col, flex:1, fontWeight:500, color:C.ink, fontSize:15 }}>{m.Navn}</span>
                  {YEARS.map(y => (
                    <span key={y} style={{ ...st.col, ...st.cYr }}>
                      <PayBadge status={payStatus(m[y])} />
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>

      </main>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
function SectionHead({ title }) {
  return (
    <div style={st.sHead}>
      <span style={st.sTitle}>{title}</span>
    </div>
  );
}

function PayBadge({ status }) {
  if (status === "paid")   return <span style={{ ...bdg, background:C.paidBg,   color:C.paidFg   }}>✓ Betalt</span>;
  if (status === "unpaid") return <span style={{ ...bdg, background:C.unpaidBg, color:C.unpaidFg }}>✗ Ikke betalt</span>;
  return null;
}
const bdg = { fontSize:12, fontWeight:600, padding:"3px 10px", borderRadius:99, whiteSpace:"nowrap" };

// ─────────────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────────────
const st = {
  root: {
    fontFamily:"'Noto Sans','Segoe UI',system-ui,sans-serif",
    background:C.canvas, height:"100vh",
    display:"flex", flexDirection:"column",
    color:C.ink, overflow:"hidden",
  },
  bar: {
    background:C.bark,
    padding:`${sp.sm}px ${sp.xl}px`,
    display:"flex", alignItems:"center", justifyContent:"space-between",
    gap:sp.lg, flexShrink:0,
    borderBottom:"1px solid rgba(255,255,255,0.05)",
  },
  barName:  { display:"flex", flexDirection:"column", gap:3, flex:"0 0 auto" },
  barTitle: { fontSize:22, fontWeight:700, color:"#F5EDD6", letterSpacing:"0.01em" },
  barDate:  { fontSize:13, color:C.inkFaint, textTransform:"capitalize" },
  wCard: {
    flex:"0 0 auto", display:"flex", alignItems:"center", gap:sp.sm,
    background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.09)",
    borderRadius:sp.sm, padding:`${sp.xs}px ${sp.md}px`,
  },
  wBody:  { display:"flex", flexDirection:"column", gap:2 },
  wTemp:  { fontSize:28, fontWeight:700, color:"#F5EDD6", lineHeight:1 },
  wDesc:  { fontSize:13, color:C.inkFaint, fontWeight:500 },
  wMeta:  { display:"flex", gap:sp.sm, fontSize:12, color:C.inkFaint, alignItems:"center", marginTop:1 },
  wClock: { fontSize:18, fontWeight:700, color:"#FFFFFF", fontVariantNumeric:"tabular-nums", marginLeft:sp.xs },
  cCard: {
    flex:"0 0 auto", display:"flex", flexDirection:"column", alignItems:"flex-end",
    background:C.goldBg, border:`1px solid ${C.goldBorder}`,
    borderRadius:sp.sm, padding:`${sp.xs}px ${sp.md}px`,
  },
  cLabel: { fontSize:10, color:"rgba(240,217,168,0.65)", textTransform:"uppercase", letterSpacing:"0.08em" },
  cMins:  { fontSize:42, fontWeight:700, color:"#F5EDD6", fontVariantNumeric:"tabular-nums", lineHeight:1.05 },
  cName:  { fontSize:16, fontWeight:700, color:"rgba(240,217,168,0.90)", marginTop:2 },

  main:   { display:"flex", flex:1, overflow:"hidden" },
  left:   { width:"50%", display:"flex", flexDirection:"column", overflow:"hidden", background:C.surface, borderRight:`1px solid ${C.rule}` },
  right:  { width:"50%", display:"flex", flexDirection:"column", overflow:"hidden", background:C.white },
  sHead: {
    display:"flex", alignItems:"baseline", justifyContent:"space-between",
    gap:sp.sm, padding:`${sp.xs}px ${sp.md}px`,
    background:C.canvas, borderBottom:`1px solid ${C.ruleMed}`,
    flexShrink:0,
  },
  sTitle: { fontSize:11, fontWeight:700, color:C.inkMuted, letterSpacing:"0.07em", textTransform:"uppercase" },
  sSub:   { fontSize:11, color:C.inkFaint },
  evList: { padding:`${sp.sm}px ${sp.md}px`, display:"flex", flexDirection:"column", gap:sp.xs, flexShrink:0 },
  empty:  { fontSize:13, color:C.inkMuted, margin:0 },
  evCard: {
    display:"flex", gap:sp.sm, background:C.white,
    borderRadius:sp.xs, border:`1px solid ${C.rule}`,
    padding:`${sp.sm}px`, alignItems:"flex-start",
  },
  evBadge: {
    flexShrink:0, background:C.bark, color:"#F5EDD6",
    fontSize:11, fontWeight:700, borderRadius:6, padding:"4px 9px",
    whiteSpace:"nowrap", alignSelf:"flex-start", marginTop:2,
  },
  evBody:    { display:"flex", flexDirection:"column", gap:2, minWidth:0 },
  evTitle:   { fontSize:15, fontWeight:700, color:C.ink },
  evTitleTR: { fontSize:12, color:C.inkMuted, fontStyle:"italic" },
  evText:    { fontSize:13, color:C.inkBody, lineHeight:1.5 },
  evTextTR:  { fontSize:12, color:C.inkMuted, fontStyle:"italic", lineHeight:1.4 },
  prayerWrap: { flexShrink:0, borderTop:`1px solid ${C.rule}` },
  prayerGrid: {
    display:"flex", flexDirection:"column",
    padding:`0 ${sp.md}px`, gap:0,
  },
  prayerRow: {
    display:"flex", alignItems:"center", justifyContent:"space-between",
    padding:`10px ${sp.sm}px`, borderRadius:0,
    position:"relative", overflow:"hidden", minHeight:42,
    borderBottom:`1px solid rgba(44,28,8,0.09)`,
  },
  prayerActive: { background:"rgba(184,131,42,0.13)", borderLeft:"3px solid #B8832A", paddingLeft:sp.sm-3 },
  prayerAccent: { display:"none" },
  prayerName: { fontSize:17, fontWeight:600, color:"#2A1D0E", paddingLeft:sp.sm },
  prayerTime: { fontSize:18, fontWeight:700, color:"#2A1D0E", fontVariantNumeric:"tabular-nums" },
  boardWrap:  { flexShrink:0, borderTop:`1px solid ${C.rule}`, background:C.canvas },
  boardGrid: {
    display:"grid", gridTemplateColumns:"repeat(3,1fr)",
    gap:`${sp.xs}px ${sp.sm}px`,
    padding:`${sp.sm}px ${sp.md}px ${sp.md}px`,
  },
  boardCard: {
    display:"flex", flexDirection:"column", gap:2,
    background:C.white, border:`1px solid ${C.rule}`,
    borderRadius:sp.xs, padding:`${sp.sm}px`,
  },
  boardRoleLine: { display:"flex", alignItems:"center", gap:5, flexWrap:"wrap" },
  boardRoleTR:   { fontSize:12, fontWeight:700, color:C.ink },
  boardRoleSep:  { fontSize:11, color:C.inkFaint },
  boardRoleDA:   { fontSize:11, color:C.inkMuted, fontStyle:"italic" },
  boardName:     { fontSize:14, fontWeight:600, color:C.inkBody, marginTop:2 },
  boardPhone:    { fontSize:12, color:C.inkFaint },
  tHead: {
    display:"flex", alignItems:"center",
    padding:`${sp.xs}px ${sp.md}px`,
    background:C.bark, color:"rgba(245,237,214,0.65)",
    fontSize:11, fontWeight:700, letterSpacing:"0.06em", textTransform:"uppercase",
    gap:sp.xs, flexShrink:0,
  },
  scrollOuter: { flex:1, overflow:"hidden" },
  mRow: {
    display:"flex", alignItems:"center",
    padding:`${sp.xs}px ${sp.md}px`,
    gap:sp.xs, borderBottom:`1px solid ${C.rule}`,
  },
  col:  { display:"flex", alignItems:"center" },
  cNo:  { width:44, flexShrink:0 },
  cYr:  { width:112, flexShrink:0, justifyContent:"center" },
};